class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    this.animations = this.content ? this.content.getAnimations() : [];

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.closeTimeout = null;
    this.setupHover();
  }

  setupHover() {
    this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    this.addEventListener('focusin', this.onFocusIn.bind(this));

    const summary = this.mainDetailsToggle.querySelector('summary');
    if (summary) {
      summary.addEventListener('click', this.onSummaryClick.bind(this));
    }

    // Close submenus when hovering or focusing level-2 items that do not have submenus
    const level2Links = this.querySelectorAll('.header__submenu--two-columns > li > a');
    level2Links.forEach(link => {
      const closeSiblings = () => {
        this.querySelectorAll('header-submenu').forEach(submenu => {
          submenu.close();
        });
      };
      link.addEventListener('mouseenter', closeSiblings);
      link.addEventListener('focusin', closeSiblings);
    });
  }

  onMouseEnter(event) {
    if (!window.matchMedia('(min-width: 990px)').matches) return;
    
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
    
    // Instantly close all other top-level header menus
    document.querySelectorAll('header-menu').forEach(menu => {
      if (menu !== this) {
        if (menu.closeTimeout) {
          clearTimeout(menu.closeTimeout);
          menu.closeTimeout = null;
        }
        menu.close();
      }
    });

    this.openMenu();
  }

  onMouseLeave(event) {
    if (!window.matchMedia('(min-width: 990px)').matches) return;
    if (event && event.relatedTarget && this.contains(event.relatedTarget)) return;
    
    if (this.closeTimeout) clearTimeout(this.closeTimeout);
    this.closeTimeout = setTimeout(() => {
      this.close();
    }, 350); // 350ms delay for top-level dropdowns
  }

  onFocusIn(event) {
    if (!window.matchMedia('(min-width: 990px)').matches) return;
    
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
    
    document.querySelectorAll('header-menu').forEach(menu => {
      if (menu !== this) menu.close();
    });

    this.openMenu();
  }

  onSummaryClick(event) {
    if (!window.matchMedia('(min-width: 990px)').matches) return;

    if (!this.mainDetailsToggle.hasAttribute('open')) {
      event.preventDefault();
      this.openMenu();
      return;
    }

    const url = event.currentTarget.getAttribute('data-url');
    if (url && url !== '#') {
      event.preventDefault();
      window.location.href = url;
    }
  }

  close() {
    super.close();
    const parentUl = this.querySelector('.header__submenu--two-columns');
    if (parentUl) {
      parentUl.style.minHeight = '';
    }
    this.onToggle();
  }

  openMenu() {
    this.mainDetailsToggle.setAttribute('open', '');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', 'true');
    this.onToggle();
  }

  onToggle() {
    super.onToggle();
    if (!this.header) this.header = document.querySelector('.header-wrapper');
    const anyOpen = !!document.querySelector('.section-header details[open]');
    if (this.header) this.header.preventHide = anyOpen;
    const stickyHeader = document.querySelector('sticky-header');
    if (stickyHeader) stickyHeader.preventHide = anyOpen;
    const sectionHeader = document.querySelector('.section-header');
    if (sectionHeader) sectionHeader.preventHide = anyOpen;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    if (this.header) {
      document.documentElement.style.setProperty(
        '--header-bottom-position-desktop',
        `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
      );
    }
  }
}

customElements.define('header-menu', HeaderMenu);

class HeaderSubMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.closeTimeout = null;
    this.setupHover();
  }

  setupHover() {
    this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    this.addEventListener('focusin', this.onFocusIn.bind(this));

    const summary = this.mainDetailsToggle.querySelector('summary');
    if (summary) {
      summary.addEventListener('click', this.onSummaryClick.bind(this));
    }
  }

  onMouseEnter(event) {
    if (!window.matchMedia('(min-width: 990px)').matches) return;

    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }

    // Instantly close all other sibling submenus in the same parent dropdown
    const parentMenu = this.closest('header-menu');
    if (parentMenu) {
      if (parentMenu.closeTimeout) {
        clearTimeout(parentMenu.closeTimeout);
        parentMenu.closeTimeout = null;
      }
      parentMenu.querySelectorAll('header-submenu').forEach(submenu => {
        if (submenu !== this) {
          if (submenu.closeTimeout) {
            clearTimeout(submenu.closeTimeout);
            submenu.closeTimeout = null;
          }
          submenu.close();
        }
      });
    }

    this.openMenu();
  }

  onMouseLeave(event) {
    if (!window.matchMedia('(min-width: 990px)').matches) return;
    const parentMenu = this.closest('header-menu');
    if (event && event.relatedTarget && (this.contains(event.relatedTarget) || (parentMenu && parentMenu.contains(event.relatedTarget)))) return;

    if (this.closeTimeout) clearTimeout(this.closeTimeout);
    this.closeTimeout = setTimeout(() => {
      this.close();
    }, 300); // 300ms delay for secondary submenus
  }

  onFocusIn(event) {
    if (!window.matchMedia('(min-width: 990px)').matches) return;

    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }

    const parentMenu = this.closest('header-menu');
    if (parentMenu) {
      parentMenu.querySelectorAll('header-submenu').forEach(submenu => {
        if (submenu !== this) submenu.close();
      });
    }

    this.openMenu();
  }

  onSummaryClick(event) {
    if (!window.matchMedia('(min-width: 990px)').matches) return;

    if (!this.mainDetailsToggle.hasAttribute('open')) {
      event.preventDefault();
      this.openMenu();
      return;
    }

    const url = event.currentTarget.getAttribute('data-url');
    if (url && url !== '#') {
      event.preventDefault();
      window.location.href = url;
    }
  }

  close() {
    super.close();
    this.resetParentHeight();
    this.onToggle();
  }

  openMenu() {
    this.mainDetailsToggle.setAttribute('open', '');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', 'true');
    this.adjustParentHeight();
    this.onToggle();
  }

  onToggle() {
    super.onToggle();
    const anyOpen = !!document.querySelector('.section-header details[open]');
    const headerWrapper = document.querySelector('.header-wrapper');
    if (headerWrapper) headerWrapper.preventHide = anyOpen;
    const stickyHeader = document.querySelector('sticky-header');
    if (stickyHeader) stickyHeader.preventHide = anyOpen;
    const sectionHeader = document.querySelector('.section-header');
    if (sectionHeader) sectionHeader.preventHide = anyOpen;
  }

  adjustParentHeight() {
    const nestedUl = this.querySelector('.header__submenu');
    const parentUl = this.closest('.header__submenu--two-columns');
    if (nestedUl && parentUl) {
      setTimeout(() => {
        if (this.mainDetailsToggle.hasAttribute('open')) {
          const nestedHeight = nestedUl.scrollHeight + 16;
          parentUl.style.minHeight = '';
          const naturalHeight = parentUl.offsetHeight;
          const targetHeight = Math.max(naturalHeight, nestedHeight);
          parentUl.style.minHeight = `${targetHeight}px`;
        }
      }, 50);
    }
  }

  resetParentHeight() {
    const parentUl = this.closest('.header__submenu--two-columns');
    if (parentUl) {
      setTimeout(() => {
        const anyOpen = parentUl.querySelector('details[open]');
        if (!anyOpen) {
          parentUl.style.minHeight = '';
        }
      }, 100);
    }
  }
}

customElements.define('header-submenu', HeaderSubMenu);
